import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertCircle, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, ChevronsUpDown,
  Download, Filter, RefreshCw, Search, ShieldAlert, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const API = getBackendUrl();

function authHeader() {
  const token = localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const TARGET_META = {
  product_master: {
    label: 'Product Master',
    colour: 'bg-violet-50 border-violet-200',
    note: null,
  },
  site_override: {
    label: 'Site Override',
    colour: 'bg-amber-50 border-amber-200',
    note: 'This updates the selected site override only. Product Master is unchanged.',
  },
  client_override: {
    label: 'Client Override',
    colour: 'bg-orange-50 border-orange-200',
    note: 'This updates site overrides for ALL sites belonging to the selected client. Product Master is unchanged.',
  },
};

export default function BulkMeasureAssignPage() {
  const navigate = useNavigate();
  useAuth(); // ensure auth context is available

  // ── Filter state ──────────────────────────────────────────────────────────
  const [target, setTarget] = useState('product_master');
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('__all__');
  const [filterCatId, setFilterCatId] = useState(null);     // taxonomy category ID (preferred)
  const [filterSub, setFilterSub] = useState('__all__');     // taxonomy subcategory name
  const [filterSubId, setFilterSubId] = useState(null);     // taxonomy subcategory ID (preferred)
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterSite, setFilterSite] = useState('__none__');
  const [filterClient, setFilterClient] = useState('__none__');
  const [unitTypes, setUnitTypes] = useState([]);
  const [filterMeasure, setFilterMeasure] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [search, setSearch] = useState('');

  // B5: searchable popover open states
  const [siteOpen, setSiteOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [actionSiteOpen, setActionSiteOpen] = useState(false);
  const [actionClientOpen, setActionClientOpen] = useState(false);

  // B1: all subcategory names across all categories (for subcat filter without a category selected)
  const allSubcats = useMemo(() => {
    const names = new Set();
    categories.forEach(c => (c.subcategories || []).forEach(s => names.add(s)));
    return [...names].sort();
  }, [categories]);

  // Subcategories to show in filter: all if no category chosen, else category-specific
  const displayedSubcats = useMemo(() => {
    if (filterCat === '__all__') return allSubcats;
    const cat = categories.find(c => c.category === filterCat);
    return cat?.subcategories || [];
  }, [filterCat, categories, allSubcats]);

  // ── Products table ────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;
  const [loadingProds, setLoadingProds] = useState(false);

  // ── Cross-page selection ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectingAll, setSelectingAll] = useState(false);

  // ── Bulk action ───────────────────────────────────────────────────────────
  const [assignUnit, setAssignUnit] = useState('__none__');
  const [setSales, setSetSales] = useState(true);
  const [setDefault, setSetDefault] = useState(true);
  const [actionSite, setActionSite] = useState('__none__');
  const [actionClient, setActionClient] = useState('__none__');

  // ── Apply state ───────────────────────────────────────────────────────────
  const [preview, setPreview] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  // ── Runs history ──────────────────────────────────────────────────────────
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  // ── Load reference data ───────────────────────────────────────────────────
  useEffect(() => {
    Promise.allSettled([
      fetch(`${API}/api/admin/bulk-measure/categories`, { headers: authHeader() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/admin/bulk-measure/unit-types`, { headers: authHeader() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/admin/bulk-measure/sites`, { headers: authHeader() }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/admin/bulk-measure/clients`, { headers: authHeader() }).then(r => r.ok ? r.json() : []),
    ]).then(results => {
      const [cats, ut, sts, cls] = results.map(r => r.status === 'fulfilled' ? r.value : []);
      setCategories(Array.isArray(cats) ? cats : []);
      // B4: unit-types now returns one row per unit_type with canonical conversion from
      // Measure Master. The dedup below remains as a safety guard.
      const rawUt = Array.isArray(ut) ? ut : [];
      const seen = new Set();
      setUnitTypes(rawUt.filter(u => { if (seen.has(u.unit_type)) return false; seen.add(u.unit_type); return true; }));
      setSites(Array.isArray(sts) ? sts : []);
      setClients(Array.isArray(cls) ? cls : []);
    }).catch(() => toast.error('Failed to load reference data'));
    loadRuns();
  }, []);

  // ── Load products ─────────────────────────────────────────────────────────
  const loadProducts = useCallback(async (resetPage = false) => {
    const offset = resetPage ? 0 : page * PAGE_SIZE;
    if (resetPage) setPage(0);
    setLoadingProds(true);
    try {
      const body = {
        // B1+B2+B6: prefer category_id (taxonomy ID) when available, fall back to name string
        category_id: filterCatId !== null ? filterCatId : undefined,
        category: filterCatId === null && filterCat !== '__all__' ? filterCat : null,
        // B1+B2: prefer subcategory_id (taxonomy ID) when available, fall back to name string
        subcategory_id: filterSubId !== null ? filterSubId : undefined,
        subcategory: filterSubId === null && filterSub !== '__all__' ? filterSub : null,
        site_id: filterSite !== '__none__' ? parseInt(filterSite) : null,
        client_id: filterClient !== '__none__' ? parseInt(filterClient) : null,
        search: search || null,
        current_measure_type: filterMeasure !== '__all__' ? filterMeasure : null,
        sales_status: filterStatus !== '__all__' ? filterStatus : null,
        limit: PAGE_SIZE,
        offset,
      };
      const r = await fetch(`${API}/api/admin/bulk-measure/products`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(body),
      });
      const d = await r.json();
      setProducts(d.products || []);
      setTotal(d.total || 0);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoadingProds(false);
    }
  }, [filterCat, filterCatId, filterSub, filterSite, filterClient, filterMeasure, filterStatus, search, page]);

  useEffect(() => { loadProducts(); }, [page]);

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const r = await fetch(`${API}/api/admin/bulk-measure/runs`, { headers: authHeader() });
      const data = await r.json();
      setRuns(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    finally { setLoadingRuns(false); }
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleId = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const selectPage = () => {
    const next = new Set(selectedIds);
    products.forEach(p => next.add(p.id));
    setSelectedIds(next);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const body = {
        category_id: filterCatId !== null ? filterCatId : undefined,
        category: filterCatId === null && filterCat !== '__all__' ? filterCat : null,
        subcategory_id: filterSubId !== null ? filterSubId : undefined,
        subcategory: filterSubId === null && filterSub !== '__all__' ? filterSub : null,
        site_id: filterSite !== '__none__' ? parseInt(filterSite) : null,
        client_id: filterClient !== '__none__' ? parseInt(filterClient) : null,
        search: search || null,
        current_measure_type: filterMeasure !== '__all__' ? filterMeasure : null,
        sales_status: filterStatus !== '__all__' ? filterStatus : null,
        limit: 9999,
        offset: 0,
      };
      const r = await fetch(`${API}/api/admin/bulk-measure/products/all-ids`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json();
      setSelectedIds(new Set(d.ids || []));
      toast.success(`Selected ${d.total} products across all pages`);
    } catch { toast.error('Select All failed'); }
    finally { setSelectingAll(false); }
  };

  // ── Preview ────────────────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one product'); return; }
    if (assignUnit === '__none__') { toast.error('Choose a measure unit to assign'); return; }
    if (target === 'site_override' && actionSite === '__none__') { toast.error('Choose a site'); return; }
    if (target === 'client_override' && actionClient === '__none__') { toast.error('Choose a client'); return; }

    setApplying(true);
    try {
      const body = {
        target,
        product_ids: [...selectedIds],
        unit_type: assignUnit,
        set_sales: setSales,
        set_default: setDefault,
        site_id: actionSite !== '__none__' ? parseInt(actionSite) : null,
        client_id: actionClient !== '__none__' ? parseInt(actionClient) : null,
        dry_run: true,
      };
      const r = await fetch(`${API}/api/admin/bulk-measure/preview`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Preview failed'); return; }
      setPreview(d);
      setShowConfirm(true);
    } catch { toast.error('Preview request failed'); }
    finally { setApplying(false); }
  };

  // ── Apply ──────────────────────────────────────────────────────────────────
  const handleApply = async () => {
    setApplying(true);
    try {
      const body = {
        target,
        product_ids: [...selectedIds],
        unit_type: assignUnit,
        set_sales: setSales,
        set_default: setDefault,
        site_id: actionSite !== '__none__' ? parseInt(actionSite) : null,
        client_id: actionClient !== '__none__' ? parseInt(actionClient) : null,
        dry_run: false,
      };
      const r = await fetch(`${API}/api/admin/bulk-measure/apply`, {
        method: 'POST', headers: authHeader(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Apply failed'); return; }
      setResult(d);
      setShowConfirm(false);
      clearSelection();
      loadRuns();
      loadProducts(true);
      toast.success(`Applied ${d.applied} changes, ${d.skipped} skipped`);
    } catch { toast.error('Apply failed'); }
    finally { setApplying(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedCat = categories.find(c => c.category === filterCat);
  const subcategories = selectedCat?.subcategories || [];

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto" data-testid="bulk-measure-assign-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/stocktaking/dashboard')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors shrink-0"
          data-testid="bulk-measure-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900">Bulk Measure Assignment</h1>
          <p className="text-sm text-slate-500 mt-1">
            Set sales/default measures for multiple products at once
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRuns} data-testid="refresh-runs-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh History
        </Button>
      </div>

      {/* Target Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-0">
        {Object.entries(TARGET_META).map(([key, meta]) => (
          <button
            key={key}
            data-testid={`target-tab-${key}`}
            onClick={() => { setTarget(key); setResult(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              target === key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {meta.label}
          </button>
        ))}
      </div>

      {/* Target Isolation Note */}
      {TARGET_META[target].note && (
        <div className={`flex gap-2 items-start rounded-lg border p-3 text-sm ${TARGET_META[target].colour}`} data-testid="isolation-note">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
          <span className="font-medium text-amber-800">{TARGET_META[target].note}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">

        {/* ── Left: Filters + Action ────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Filter Panel */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3 bg-white">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Filter className="h-3.5 w-3.5" /> Filters
            </p>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                data-testid="bma-search"
                placeholder="Product name or code"
                className="pl-8 h-9"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadProducts(true)}
              />
            </div>

            {/* Category */}
            <Select value={filterCat} onValueChange={v => {
              setFilterCat(v);
              setFilterSub('__all__');
              setFilterSubId(null);
              if (v === '__all__') {
                setFilterCatId(null);
              } else {
                const cat = categories.find(c => c.category === v);
                setFilterCatId(cat?.category_id ?? null);
              }
            }}>
              <SelectTrigger data-testid="bma-cat-filter" className="h-9">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Subcategory — B1: works without selecting a category first */}
            <Select
              value={filterSub}
              onValueChange={v => {
                setFilterSub(v);
                if (v === '__all__') {
                  setFilterSubId(null);
                } else {
                  // B2: use taxonomy ID when available (from subcategory_ids map)
                  const cat = filterCat !== '__all__' ? categories.find(c => c.category === filterCat) : null;
                  const subId = cat?.subcategory_ids?.[v] ?? null;
                  setFilterSubId(subId);
                }
              }}
            >
              <SelectTrigger data-testid="bma-sub-filter" className="h-9">
                <SelectValue placeholder="All subcategories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All subcategories</SelectItem>
                {displayedSubcats.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Site scope — B5: searchable combobox */}
            <Popover open={siteOpen} onOpenChange={setSiteOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={siteOpen}
                  className="h-9 justify-between font-normal text-sm"
                  data-testid="bma-site-filter"
                >
                  {filterSite !== '__none__'
                    ? (sites.find(s => String(s.id) === filterSite)?.name ?? filterSite)
                    : 'All sites'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0">
                <Command>
                  <CommandInput placeholder="Search sites…" />
                  <CommandEmpty>No site found.</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-y-auto">
                    <CommandItem value="__none__" onSelect={() => { setFilterSite('__none__'); setSiteOpen(false); }}>
                      All sites
                    </CommandItem>
                    {sites.map(s => (
                      <CommandItem key={s.id} value={s.name} onSelect={() => { setFilterSite(String(s.id)); setSiteOpen(false); }}>
                        {s.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Client scope — B5: searchable combobox */}
            <Popover open={clientOpen} onOpenChange={setClientOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientOpen}
                  className="h-9 justify-between font-normal text-sm"
                  data-testid="bma-client-filter"
                >
                  {filterClient !== '__none__'
                    ? (clients.find(c => String(c.id) === filterClient)?.name ?? filterClient)
                    : 'All clients'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0">
                <Command>
                  <CommandInput placeholder="Search clients…" />
                  <CommandEmpty>No client found.</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-y-auto">
                    <CommandItem value="__none__" onSelect={() => { setFilterClient('__none__'); setClientOpen(false); }}>
                      All clients
                    </CommandItem>
                    {clients.map(c => (
                      <CommandItem key={c.id} value={c.name} onSelect={() => { setFilterClient(String(c.id)); setClientOpen(false); }}>
                        {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Current measure filter */}
            <Select value={filterMeasure} onValueChange={setFilterMeasure}>
              <SelectTrigger data-testid="bma-measure-filter" className="h-9">
                <SelectValue placeholder="All measures" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All measures</SelectItem>
                {unitTypes.map(u => <SelectItem key={u.unit_type} value={u.unit_type}>{u.unit_type}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Default/Sales status filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="bma-status-filter" className="h-9">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                <SelectItem value="has_default">Has default sales measure</SelectItem>
                <SelectItem value="no_default">No default sales measure</SelectItem>
                <SelectItem value="has_sales">Has any sales unit</SelectItem>
                <SelectItem value="no_sales">No sales unit assigned</SelectItem>
              </SelectContent>
            </Select>

            <Button
              data-testid="bma-apply-filters-btn"
              className="w-full h-9"
              onClick={() => loadProducts(true)}
              disabled={loadingProds}
            >
              {loadingProds ? 'Loading…' : 'Apply Filters'}
            </Button>
          </div>

          {/* Bulk Action Panel */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3 bg-white">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Zap className="h-3.5 w-3.5" /> Bulk Action
            </p>

            {/* Measure unit to assign */}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Assign measure</label>
              <Select value={assignUnit} onValueChange={setAssignUnit}>
                <SelectTrigger className="h-9" data-testid="bma-assign-unit-trigger">
                  <SelectValue placeholder="Choose unit type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— choose unit type —</SelectItem>
                  {unitTypes.map(u => (
                    <SelectItem key={u.unit_type} value={u.unit_type}>
                      {u.unit_type} {u.conversion_to_base ? `(${u.conversion_to_base}ml)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Flags */}
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={setSales} onChange={e => setSetSales(e.target.checked)}
                  data-testid="bma-set-sales-cb" />
                Set as Sales
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={setDefault} onChange={e => setSetDefault(e.target.checked)}
                  data-testid="bma-set-default-cb" />
                Set as Default
              </label>
            </div>

            {/* Site selector (for site_override) — B5: searchable */}
            {target === 'site_override' && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Target site</label>
                <Popover open={actionSiteOpen} onOpenChange={setActionSiteOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={actionSiteOpen}
                      className="h-9 w-full justify-between font-normal text-sm"
                      data-testid="bma-action-site"
                    >
                      {actionSite !== '__none__'
                        ? (sites.find(s => String(s.id) === actionSite)?.name ?? actionSite)
                        : '— choose site —'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0">
                    <Command>
                      <CommandInput placeholder="Search sites…" />
                      <CommandEmpty>No site found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-y-auto">
                        <CommandItem value="__none__" onSelect={() => { setActionSite('__none__'); setActionSiteOpen(false); }}>
                          — choose site —
                        </CommandItem>
                        {sites.map(s => (
                          <CommandItem key={s.id} value={s.name} onSelect={() => { setActionSite(String(s.id)); setActionSiteOpen(false); }}>
                            {s.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Client selector (for client_override) — B5: searchable */}
            {target === 'client_override' && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Target client</label>
                <Popover open={actionClientOpen} onOpenChange={setActionClientOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={actionClientOpen}
                      className="h-9 w-full justify-between font-normal text-sm"
                      data-testid="bma-action-client"
                    >
                      {actionClient !== '__none__'
                        ? (clients.find(c => String(c.id) === actionClient)?.name ?? actionClient)
                        : '— choose client —'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0">
                    <Command>
                      <CommandInput placeholder="Search clients…" />
                      <CommandEmpty>No client found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-y-auto">
                        <CommandItem value="__none__" onSelect={() => { setActionClient('__none__'); setActionClientOpen(false); }}>
                          — choose client —
                        </CommandItem>
                        {clients.map(c => (
                          <CommandItem key={c.id} value={c.name} onSelect={() => { setActionClient(String(c.id)); setActionClientOpen(false); }}>
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="pt-1 space-y-2">
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                <span className="font-semibold">{selectedIds.size}</span> product{selectedIds.size !== 1 ? 's' : ''} selected
                {selectedIds.size > 0 && (
                  <button onClick={clearSelection} className="ml-2 text-slate-400 hover:text-slate-700 underline text-xs">
                    Clear
                  </button>
                )}
              </div>
              <Button
                data-testid="bma-preview-btn"
                className="w-full h-9 bg-slate-900 hover:bg-slate-700 text-white"
                onClick={handlePreview}
                disabled={applying || selectedIds.size === 0 || assignUnit === '__none__'}
              >
                {applying ? 'Building preview…' : `Preview ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Right: Products table ─────────────────────────────────────── */}
        <div className="space-y-3">

          {/* Selection controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" data-testid="bma-select-page-btn" onClick={selectPage}>
              Select page
            </Button>
            <Button variant="outline" size="sm" data-testid="bma-select-all-btn"
              onClick={selectAllFiltered} disabled={selectingAll}>
              {selectingAll ? 'Loading…' : `Select all filtered (${total})`}
            </Button>
            {selectedIds.size > 0 && (
              <Button variant="ghost" size="sm" data-testid="bma-clear-btn" onClick={clearSelection}>
                Clear ({selectedIds.size})
              </Button>
            )}
            <span className="ml-auto text-sm text-slate-500">{total} products found</span>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid="bma-products-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="w-8 p-2">
                    <input type="checkbox"
                      checked={products.length > 0 && products.every(p => selectedIds.has(p.id))}
                      onChange={e => e.target.checked ? selectPage() : clearSelection()}
                      data-testid="bma-header-cb"
                    />
                  </th>
                  <th className="text-left p-2 text-xs font-semibold text-slate-500">Code</th>
                  <th className="text-left p-2 text-xs font-semibold text-slate-500">Product</th>
                  <th className="text-left p-2 text-xs font-semibold text-slate-500">Category</th>
                  <th className="text-left p-2 text-xs font-semibold text-slate-500">Current Default Sales</th>
                  <th className="text-left p-2 text-xs font-semibold text-slate-500">All Sales Units</th>
                </tr>
              </thead>
              <tbody>
                {loadingProds ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400">No products match the current filters.</td></tr>
                ) : products.map((p, i) => (
                  <tr key={p.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedIds.has(p.id) ? 'bg-violet-50' : ''}`}
                    data-testid={`bma-row-${p.id}`}
                  >
                    <td className="p-2">
                      <input type="checkbox"
                        data-testid={`bma-cb-${p.id}`}
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleId(p.id)}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs text-slate-500">{p.product_code || '—'}</td>
                    <td className="p-2 font-medium text-slate-800">{p.product_name}</td>
                    <td className="p-2">
                      <span className="text-xs text-slate-500">{p.category || '—'}</span>
                      {p.subcategory && <span className="text-xs text-slate-400 ml-1">/ {p.subcategory}</span>}
                    </td>
                    <td className="p-2">
                      {p.current_default_sales
                        ? <Badge variant="secondary" className="text-xs">{p.current_default_sales}</Badge>
                        : <span className="text-slate-400 text-xs">none</span>}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {p.units.filter(u => u.is_sales_unit).map(u => (
                          <span key={u.id} className="text-xs bg-slate-100 rounded px-1">{u.unit_type}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-slate-500" data-testid="bma-pagination">
            <span>Page {page + 1} of {totalPages} · {total} products</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" data-testid="bma-prev-btn"
                disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const p = totalPages <= 7 ? i : Math.max(0, Math.min(page - 3, totalPages - 7)) + i;
                return (
                  <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                    className="w-8 h-8 p-0" onClick={() => setPage(p)}
                    data-testid={`bma-page-btn-${p + 1}`}>
                    {p + 1}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" data-testid="bma-next-btn"
                disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Apply Result */}
      {result && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2" data-testid="bma-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-green-800">
              Applied {result.applied} change{result.applied !== 1 ? 's' : ''}, {result.skipped} skipped
            </span>
          </div>
          <p className="text-xs text-green-700">{result.site_isolation?.note}</p>
        </div>
      )}

      {/* Run History */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="bma-runs-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700">Recent Runs</h3>
        </div>
        {loadingRuns ? (
          <p className="p-4 text-center text-slate-400 text-sm">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="p-4 text-center text-slate-400 text-sm">No runs yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-semibold text-slate-500">Run ID</th>
                <th className="text-left p-3 font-semibold text-slate-500">Target</th>
                <th className="text-left p-3 font-semibold text-slate-500">Unit Type</th>
                <th className="text-left p-3 font-semibold text-slate-500">Applied</th>
                <th className="text-left p-3 font-semibold text-slate-500">When</th>
                <th className="text-left p-3 font-semibold text-slate-500">Export</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => (
                <tr key={run.run_id} className="border-b border-slate-100" data-testid={`bma-run-${run.run_id.slice(0, 8)}`}>
                  <td className="p-3 font-mono">{run.run_id.slice(0, 12)}…</td>
                  <td className="p-3"><Badge variant="outline">{run.target}</Badge></td>
                  <td className="p-3">{run.unit_type}</td>
                  <td className="p-3 font-semibold">{run.applied}</td>
                  <td className="p-3 text-slate-400">{run.created_at?.slice(0, 19)?.replace('T', ' ')}</td>
                  <td className="p-3">
                    <a
                      data-testid={`bma-export-${run.run_id.slice(0, 8)}`}
                      href={`${API}/api/admin/bulk-measure/runs/${run.run_id}/export`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-slate-500 hover:text-slate-800"
                    >
                      <Download className="h-3.5 w-3.5" /> CSV
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Measure Assignment</DialogTitle>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 text-sm">
              {/* Summary counts — split no_uom from already_set clearly */}
              <div className={`grid gap-3 ${(preview.no_uom || 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">{preview.applied}</p>
                  <p className="text-xs text-slate-500 mt-1">Would change</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-500">{preview.already_set ?? preview.skipped}</p>
                  <p className="text-xs text-slate-500 mt-1">Already correct</p>
                </div>
                {(preview.no_uom || 0) > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center" data-testid="bma-no-uom-count">
                    <p className="text-2xl font-bold text-amber-700">{preview.no_uom}</p>
                    <p className="text-xs text-amber-600 mt-1">Missing UOM type</p>
                  </div>
                )}
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-lg font-bold text-slate-700">{preview.unit_type}</p>
                  <p className="text-xs text-slate-500 mt-1">Unit type</p>
                </div>
              </div>

              {/* Explain no_uom rows so admin understands the action required */}
              {(preview.no_uom || 0) > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="bma-no-uom-notice">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-xs">
                    <strong>{preview.no_uom} product{preview.no_uom !== 1 ? 's' : ''}</strong> have no UOM row
                    of type <strong>{preview.unit_type}</strong> — they cannot be assigned by this tool until a UOM
                    row of this type is added via Product Management. These are listed below with status{' '}
                    <strong>no_uom</strong> and are excluded from Apply.
                  </p>
                </div>
              )}

              {TARGET_META[target].note && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <ShieldAlert className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-xs">{TARGET_META[target].note}</p>
                </div>
              )}

              <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
                {preview.site_isolation?.note}
              </div>

              <div className="max-h-48 overflow-y-auto rounded border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Before</th>
                      <th className="text-left p-2">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changes?.slice(0, 50).map((ch, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${ch.status === 'no_uom' ? 'bg-amber-50' : ''}`}>
                        <td className="p-2 font-mono">{ch.product_code || '—'}</td>
                        <td className="p-2">{ch.product_name}</td>
                        <td className="p-2">
                          <Badge
                            variant={ch.status === 'would_apply' ? 'default' : ch.status === 'no_uom' ? 'outline' : 'secondary'}
                            className={`text-xs ${ch.status === 'no_uom' ? 'border-amber-400 text-amber-700 bg-amber-50' : ''}`}
                          >
                            {ch.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-slate-500">
                          {ch.status === 'no_uom'
                            ? <span className="italic text-amber-600">No UOM row — cannot assign</span>
                            : JSON.stringify(ch.before)}
                        </td>
                        <td className="p-2 text-slate-700">
                          {ch.status === 'no_uom'
                            ? <span className="text-amber-600 text-xs">Add via Product Mgmt first</span>
                            : JSON.stringify(ch.after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={applying}>Cancel</Button>
            <Button
              data-testid="bma-confirm-apply-btn"
              onClick={handleApply}
              disabled={applying || (preview?.applied ?? 0) === 0}
              className="bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {applying
                ? 'Applying…'
                : (preview?.applied ?? 0) === 0
                  ? 'Nothing to apply'
                  : `Apply ${preview.applied} change${preview.applied !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
